/**
 * Legal Page Shell
 *
 * Shared layout for public legal pages.
 *
 * Primary responsibilities:
 * - Keep privacy and terms pages visually aligned
 * - Provide a consistent readable content frame
 *
 * Dependencies:
 * - marketing primitives
 */

import Link from 'next/link'
import { ArrowLeftIcon } from '@radix-ui/react-icons'

import {
  MarketingContainer,
  MarketingEyebrow,
  MarketingPage,
  MarketingSection
} from '@/components/marketing/marketing-primitives'

interface LegalPageSection {
  title: string
  body?: string
  intro?: string
  highlight?: string
  items?: string[]
}

interface LegalPageShellProps {
  eyebrow: string
  title: string
  description: string
  backLabel: string
  lastUpdated?: string
  sections: LegalPageSection[]
}

export function LegalPageShell({
  eyebrow,
  title,
  description,
  backLabel,
  lastUpdated,
  sections
}: LegalPageShellProps) {
  return (
    <MarketingPage>
      <MarketingContainer className="max-w-5xl">
        <div className="mb-6">
          <Link
            href="/"
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 transition-colors hover:text-slate-950"
          >
            <ArrowLeftIcon className="size-4" />
            {backLabel}
          </Link>
        </div>

        <header className="mb-10 max-w-[40rem]">
          <MarketingEyebrow>{eyebrow}</MarketingEyebrow>
          <h1 className="font-display mt-4 text-4xl font-medium leading-[0.98] tracking-[-0.02em] text-slate-950 sm:text-5xl dark:text-slate-50">
            {title}
          </h1>
          <p className="mt-4 text-base leading-8 text-slate-600 sm:text-lg dark:text-slate-300">
            {description}
          </p>
          {lastUpdated ? (
            <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
              Last updated: {lastUpdated}
            </p>
          ) : null}
        </header>

        <MarketingSection
          className="mt-0"
          contentClassName="grid gap-8"
        >
          {sections.map((section) => (
            <section
              key={section.title}
              className="bg-white/88 rounded-[1.75rem] border border-slate-200/80 p-6 shadow-[0_16px_40px_rgba(15,23,42,0.05)]"
            >
              <h2 className="text-2xl font-semibold tracking-tight text-slate-950">
                {section.title}
              </h2>
              {section.highlight ? (
                <p className="mt-4 text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
                  {section.highlight}
                </p>
              ) : null}
              {section.intro ? (
                <p className="mt-4 text-base leading-8 text-slate-600">{section.intro}</p>
              ) : null}
              {section.body ? (
                <p className="mt-4 text-base leading-8 text-slate-600">{section.body}</p>
              ) : null}
              {section.items?.length ? (
                <ul className="mt-5 grid gap-3">
                  {section.items.map((item) => (
                    <li
                      key={item}
                      className="rounded-2xl border border-slate-200 bg-slate-50/70 px-4 py-3 text-sm leading-7 text-slate-600"
                    >
                      {item}
                    </li>
                  ))}
                </ul>
              ) : null}
            </section>
          ))}
        </MarketingSection>
      </MarketingContainer>
    </MarketingPage>
  )
}
